(function (scope) {
  'use strict';

  function safeProxyPath(value) {
    if (typeof value !== 'string' || !/^\/apps\/[a-zA-Z0-9/_-]+$/.test(value)) return null;
    return value;
  }

  function parseReport(payload) {
    if (!payload || payload.status !== 'ready' || payload.locationConfirmed !== true || typeof payload.locationLabel !== 'string' || !payload.locationLabel.trim()) {
      throw new Error('REPORT_UNAVAILABLE');
    }
    if (!Number.isFinite(Date.parse(payload.generatedAt)) || !Array.isArray(payload.risks) || payload.risks.length === 0 || payload.risks.length > 12 || !Array.isArray(payload.actions)) throw new Error('REPORT_INVALID');
    if (!['address_match', 'area_representative'].includes(payload.locationPrecision) || typeof payload.locationNote !== 'string' || !payload.locationNote.trim() || typeof payload.granularityNote !== 'string' || !payload.granularityNote.trim()) throw new Error('REPORT_INVALID');
    const safeSource = (value) => {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password) throw new Error('REPORT_INVALID');
      return url.href;
    };
    const risks = payload.risks.map((risk) => {
      if (!risk || typeof risk.label !== 'string' || typeof risk.summary !== 'string') throw new Error('REPORT_INVALID');
      if (!['available', 'unavailable', 'not-covered'].includes(risk.state)) throw new Error('REPORT_INVALID');
      if (!['first', 'basic', 'check'].includes(risk.priority) || !Array.isArray(risk.sources) || !risk.sources.length || risk.sources.length > 6) throw new Error('REPORT_INVALID');
      const sources = risk.sources.map((source) => {
        if (!source || typeof source.name !== 'string' || !source.name.trim()) throw new Error('REPORT_INVALID');
        return { name: source.name, url: safeSource(source.url), licenseUrl: safeSource(source.licenseUrl) };
      });
      return { label: risk.label, state: risk.state, priority: risk.priority, summary: risk.summary, sourceUrl: safeSource(risk.sourceUrl), sources };
    });
    const actions = payload.actions.filter((action) => typeof action === 'string' && action.trim()).slice(0, 8);
    return { locationLabel: payload.locationLabel, locationPrecision: payload.locationPrecision, locationNote: payload.locationNote, granularityNote: payload.granularityNote, generatedAt: payload.generatedAt, risks, actions };
  }

  scope.SonamaruLocation = Object.freeze({ safeProxyPath, parseReport });
  if (typeof module !== 'undefined' && module.exports) module.exports = scope.SonamaruLocation;
  if (typeof HTMLElement === 'undefined' || !scope.customElements) return;

  class SonamaruLocation extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.querySelector('[data-location-form]');
      this.button = this.querySelector('[data-location-submit]');
      this.status = this.querySelector('[data-location-status]');
      this.result = this.querySelector('[data-location-result]');
      this.endpoint = safeProxyPath(this.dataset.endpoint);
      if (!this.endpoint) {
        this.button.disabled = true;
        this.form.querySelector('input').disabled = true;
        this.setStatus('住所レポートは準備中です。現在、住所は送信されません。');
      } else {
        this.button.disabled = false;
        this.form.querySelector('input').disabled = false;
      }
      this.onSubmit = (event) => {
        event.preventDefault();
        if (this.endpoint) this.requestReport();
      };
      this.onInput = () => {
        this.controller?.abort();
        this.controller = null;
        clearTimeout(this.requestTimeout);
        this.requestTimeout = null;
        this.form.removeAttribute('aria-busy');
        this.button.disabled = !this.endpoint;
        this.result.replaceChildren();
        this.result.hidden = true;
        this.status.hidden = true;
        this.updateShoppingContext(null);
      };
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('input', this.onInput);
      this.onResultClick = (event) => {
        if (event.target.closest('[data-location-print]')) this.printReport();
      };
      this.result.addEventListener('click', this.onResultClick);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.requestTimeout);
      this.requestTimeout = null;
      this.form?.removeAttribute('aria-busy');
      if (this.result?.hidden && this.status) this.status.hidden = true;
      this.restorePrint?.();
      this.form?.removeEventListener('submit', this.onSubmit);
      this.form?.removeEventListener('input', this.onInput);
      this.result?.removeEventListener('click', this.onResultClick);
      this.ready = false;
    }

    setStatus(message) {
      this.status.textContent = message;
      this.status.hidden = false;
    }

    printReport() {
      if (this.restorePrint || this.result.hidden) return;
      const target = this.closest('.sm-place');
      if (!target) return;
      const details = [...this.result.querySelectorAll('details')].map((node) => [node, node.open]);
      target.setAttribute('data-sonamaru-print', 'location');
      for (const [node] of details) node.open = true;
      const restore = () => {
        target.removeAttribute('data-sonamaru-print');
        for (const [node, open] of details) node.open = open;
        window.removeEventListener('afterprint', restore);
        this.restorePrint = null;
      };
      this.restorePrint = restore;
      window.addEventListener('afterprint', restore, { once: true });
      try { window.print(); }
      catch (_error) { restore(); this.setStatus('印刷画面を開けませんでした。ブラウザの印刷機能からお試しください。'); }
    }

    async requestReport() {
      const address = this.form.querySelector('[name="address"]').value.trim();
      if (address.length < 2 || address.length > 100) {
        this.setStatus('備えを整える場所の住所を入力してください。');
        return;
      }
      this.controller?.abort();
      clearTimeout(this.requestTimeout);
      this.updateShoppingContext(null);
      this.controller = new AbortController();
      const controller = this.controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      this.requestTimeout = timeout;
      this.button.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.result.replaceChildren();
      this.result.hidden = true;
      this.setStatus('この場所を確認しています。');
      try {
        const response = await fetch(this.endpoint, {
          method: 'POST', credentials: 'same-origin', cache: 'no-store', redirect: 'error',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ address }), signal: controller.signal
        });
        if (!response.ok) {
          const failure = await response.json().catch(() => ({}));
          const error = new Error('REPORT_UNAVAILABLE');
          error.code = failure.error;
          error.status = response.status;
          throw error;
        }
        const report = parseReport(await response.json());
        if (!this.isConnected || this.controller !== controller) return;
        if (controller.signal.aborted) throw new DOMException('Report request timed out', 'AbortError');
        this.renderReport(report);
        this.setStatus('この場所のレポートを表示しました。');
      } catch (error) {
        if (!this.isConnected || this.controller !== controller) return;
        const message = error.name === 'AbortError' ? '確認に時間がかかっています。少し待ってから、もう一度お試しください。'
          : error.status === 429 ? '短時間に多くの確認がありました。少し待ってから、もう一度お試しください。'
          : ['address_not_found', 'location_unconfirmed', 'invalid_address'].includes(error.code) ? '場所を確認できませんでした。都道府県から町名・番地まで入力し直してください。'
          : 'この場所のレポートを取得できませんでした。時間をおいて、もう一度お試しください。';
        this.setStatus(message);
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) {
          this.requestTimeout = null;
          this.button.disabled = false;
          this.form.removeAttribute('aria-busy');
        }
      }
    }

    renderReport(report) {
      const knownFirst = report.risks.filter(risk => risk.state === 'available' && risk.priority === 'first');
      this.updateShoppingContext({
        evacuationFirst: knownFirst.some(risk => /洪水|内水|津波|高潮/.test(risk.label)),
        evacuationRisks: knownFirst.filter(risk => /洪水|内水|津波|高潮/.test(risk.label)).map(risk => risk.label),
        groundFirst: knownFirst.some(risk => /地震|地盤|液状化/.test(risk.label)),
        groundRisks: knownFirst.filter(risk => /地震|地盤|液状化/.test(risk.label)).map(risk => risk.label),
        incomplete: report.risks.some(risk => risk.state === 'unavailable' || risk.state === 'not-covered')
      });
      this.result.replaceChildren();
      const heading = document.createElement('h4');
      heading.textContent = report.locationLabel;
      heading.tabIndex = -1;
      const precision = document.createElement('p');
      precision.className = 'sm-location-precision';
      precision.textContent = report.locationNote;
      const granularity = document.createElement('p');
      granularity.className = 'sm-muted';
      granularity.textContent = report.granularityNote;
      this.result.append(heading, precision, granularity);
      if (report.actions.length) {
        const title = document.createElement('h5');
        title.textContent = '今日、確認したいこと';
        const list = document.createElement('ul');
        list.className = 'sm-location-actions';
        for (const action of report.actions) {
          const item = document.createElement('li');
          item.textContent = action;
          list.append(item);
        }
        this.result.append(title, list);
      }
      const calculator = document.getElementById(this.dataset.calculatorId || '');
      if (calculator) {
        const next = document.createElement('a');
        next.className = 'sm-text-link';
        next.href = `#${calculator.id}`;
        next.textContent = '家族の人数から、必要な量を計算する →';
        this.result.append(next);
      }
      for (const risk of report.risks) {
        const row = document.createElement('div');
        row.className = 'sm-location-risk';
        const title = document.createElement('h5');
        title.textContent = risk.label;
        const priority = document.createElement('span');
        priority.className = `sm-location-priority sm-location-priority--${risk.priority}`;
        priority.textContent = { first: '先に確認', basic: '基本の備えを', check: '公式情報も確認' }[risk.priority];
        const summary = document.createElement('p');
        summary.textContent = risk.summary;
        const details = document.createElement('details');
        const disclosure = document.createElement('summary');
        disclosure.textContent = '出典・利用条件';
        details.append(disclosure);
        const sources = document.createElement('ul');
        for (const source of risk.sources) {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.textContent = source.name;
          link.href = source.url;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          const license = document.createElement('a');
          license.textContent = '利用条件';
          license.href = source.licenseUrl;
          license.target = '_blank';
          license.rel = 'noopener noreferrer';
          item.append(link, document.createTextNode(' ／ '), license);
          sources.append(item);
        }
        details.append(sources);
        row.append(title, priority, summary, details);
        this.result.append(row);
      }
      const updated = document.createElement('p');
      updated.className = 'sm-muted';
      updated.textContent = `レポート作成日時：${new Date(report.generatedAt).toLocaleString('ja-JP')}`;
      this.result.append(updated);
      const print = document.createElement('button');
      print.type = 'button';
      print.className = 'sm-text-button';
      print.dataset.locationPrint = '';
      print.textContent = 'この場所のレポートを印刷・PDFに保存する';
      this.result.append(print);
      this.result.hidden = false;
      heading.focus({ preventScroll: true });
    }

    updateShoppingContext(context) {
      const names = values => Object.freeze(Array.isArray(values) ? [...new Set(values.filter(value => typeof value === 'string' && value.trim()).map(value => value.slice(0, 60)))].slice(0, 12) : []);
      this.shoppingContext = context ? Object.freeze({ evacuationFirst: context.evacuationFirst === true, evacuationRisks: names(context.evacuationRisks), groundFirst: context.groundFirst === true, groundRisks: names(context.groundRisks), incomplete: context.incomplete === true }) : null;
      document.dispatchEvent(new CustomEvent('sonamaru:place-context', { detail: { calculatorId: this.dataset.calculatorId, context: this.shoppingContext } }));
    }

    // Minimal, address-free context for calculators reconnected by the theme editor.
    getShoppingContext() { return this.result?.hidden ? null : this.shoppingContext; }
  }

  if (!scope.customElements.get('sonamaru-location')) scope.customElements.define('sonamaru-location', SonamaruLocation);
})(typeof globalThis !== 'undefined' ? globalThis : window);
